import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from '../components/config.js';
import axios from 'axios';
import './CommunityResourcesPage.css';

const CommunityResourcesPage = () => {
  const [resources, setResources] = useState([]);
  const [filteredResources, setFilteredResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortMode, setSortMode] = useState('newest');
  const [selectedResource, setSelectedResource] = useState(null);
  const [disclaimer, setDisclaimer] = useState('');
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [readResourceIds, setReadResourceIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('communityResourcesRead') || '{}');
    } catch (error) {
      return {};
    }
  });

  // Helper function to get absolute URL for file
  const getFileUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  useEffect(() => {
    fetchCategories();
    fetchResources();
    fetchSettings();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('communityResourcesRead', JSON.stringify(readResourceIds));
    } catch (error) {
      console.warn('Unable to persist read resource state', error);
    }
  }, [readResourceIds]);

  useEffect(() => {
    const nextResources = selectedCategory === 'All'
      ? resources
      : resources.filter(r => r.category === selectedCategory);

    const sortedResources = [...nextResources].sort((a, b) => {
      const aUnread = isUnreadResource(a) ? 1 : 0;
      const bUnread = isUnreadResource(b) ? 1 : 0;

      if (sortMode === 'unread-first') {
        if (aUnread !== bUnread) return bUnread - aUnread;
      }

      if (sortMode === 'title-asc') {
        return (a.title || '').localeCompare(b.title || '');
      }

      if (sortMode === 'title-desc') {
        return (b.title || '').localeCompare(a.title || '');
      }

      if (sortMode === 'newest') {
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      }

      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });

    setFilteredResources(sortedResources);
  }, [selectedCategory, resources, sortMode, readResourceIds]);

  const isUnreadResource = (resource) => {
    const resourceId = resource?._id;
    const createdAt = resource?.createdAt ? new Date(resource.createdAt).getTime() : 0;

    if (!resourceId || !createdAt) return false;
    if (readResourceIds[resourceId]) return false;

    const fourteenDaysInMs = 14 * 24 * 60 * 60 * 1000;
    return (Date.now() - createdAt) <= fourteenDaysInMs;
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      setCategories(response.data.filter(cat => cat.isActive));
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchResources = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/community-resources/published`);
      setResources(response.data.resources);
      setFilteredResources(response.data.resources);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching resources:', err);
      setError('Failed to load community resources');
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/community-resources/settings`);
      if (response.data.success) {
        setDisclaimer(response.data.settings.disclaimer || '');
        setShowDisclaimer(response.data.settings.showDisclaimer || false);
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  };

  const handleSelectResource = async (resource) => {
    setReadResourceIds((prev) => ({ ...prev, [resource._id]: Date.now() }));

    try {
      const response = await axios.get(`${API_URL}/api/community-resources/published/${resource._id}`);
      setSelectedResource(response.data.resource || resource);
    } catch (err) {
      console.error('Error opening community resource:', err);
      setSelectedResource(resource);
    }
  };

  const getCategoryIcon = (categoryName) => {
    const category = categories.find(cat => cat.name === categoryName);
    return category?.icon || '📍';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="community-resources-container">
        <div className="loading">Loading community resources...</div>
      </div>
    );
  }

  return (
    <div className="community-resources-container">
      <div className="resources-header">
        <h1>Community Resources</h1>
        <p>Access programs, services, and events available to our community members</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showDisclaimer && disclaimer && (
        <div className="disclaimer-box">
          <div className="disclaimer-icon">⚠️</div>
          <div className="disclaimer-content">
            {disclaimer}
          </div>
        </div>
      )}

      <div className="toolbar-row">
        <div className="category-filters" aria-label="Resource category filters">
          <button
            className={`category-btn ${selectedCategory === 'All' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('All')}
          >
            All
          </button>
          {categories.map(category => (
            <button
              key={category._id}
              className={`category-btn ${selectedCategory === category.name ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category.name)}
            >
              {category.icon} {category.name}
            </button>
          ))}
        </div>

        <div className="sort-panel">
          <label htmlFor="public-resource-sort" className="sort-label">Sort resources</label>
          <select
            id="public-resource-sort"
            aria-label="Sort resources"
            className="sort-select"
            value={sortMode}
            onChange={(event) => setSortMode(event.target.value)}
          >
            <option value="newest">Newest first</option>
            <option value="unread-first">Unread first</option>
            <option value="title-asc">Name A–Z</option>
            <option value="title-desc">Name Z–A</option>
          </select>
        </div>
      </div>

      {selectedResource ? (
        <div className="resource-detail-view">
          <button onClick={() => setSelectedResource(null)} className="btn-back">
            ← Back to Resources
          </button>

          <div className="resource-detail">
            <div className="resource-detail-header">
              <div>
                <h2>{selectedResource.title}</h2>
                <span className="category-badge">
                  {getCategoryIcon(selectedResource.category)} {selectedResource.category}
                </span>
              </div>
              {selectedResource.isFeatured && (
                <span className="featured-badge">⭐ Featured Resource</span>
              )}
            </div>

            {selectedResource.thumbnailUrl && (
              <img 
                src={selectedResource.thumbnailUrl} 
                alt={selectedResource.title}
                className="resource-thumbnail"
              />
            )}

            {selectedResource.description && (
              <p className="resource-description">{selectedResource.description}</p>
            )}

            {selectedResource.videoUrl && (
              <div className="resource-video">
                <iframe
                  src={selectedResource.videoUrl}
                  title="Resource Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}

            <div 
              className="resource-content"
              dangerouslySetInnerHTML={{ __html: selectedResource.content }}
            />

            {selectedResource.contactInfo && (selectedResource.contactInfo.name || selectedResource.contactInfo.email || selectedResource.contactInfo.phone) && (
              <div className="contact-info-card">
                <h4>📞 Contact Information</h4>
                {selectedResource.contactInfo.name && <p><strong>Name:</strong> {selectedResource.contactInfo.name}</p>}
                {selectedResource.contactInfo.email && <p><strong>Email:</strong> <a href={`mailto:${selectedResource.contactInfo.email}`}>{selectedResource.contactInfo.email}</a></p>}
                {selectedResource.contactInfo.phone && <p><strong>Phone:</strong> <a href={`tel:${selectedResource.contactInfo.phone}`}>{selectedResource.contactInfo.phone}</a></p>}
                {selectedResource.contactInfo.address && <p><strong>Address:</strong> {selectedResource.contactInfo.address}</p>}
              </div>
            )}

            {selectedResource.attachments && selectedResource.attachments.length > 0 && (
              <div className="resource-attachments">
                <h4>📎 Attachments</h4>
                {selectedResource.attachments.map((attachment, idx) => (
                  <div key={idx} className="attachment-item">
                    {attachment.fileType === 'application/pdf' ? (
                      <div className="pdf-viewer">
                        <div className="pdf-controls">
                          <span className="pdf-filename">📄 {attachment.filename}</span>
                          <a 
                            href={getFileUrl(attachment.url)} 
                            download={attachment.filename}
                            className="btn-download"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            ⬇ Download
                          </a>
                        </div>
                        <iframe
                          src={getFileUrl(attachment.url)}
                          width="100%"
                          height="600px"
                          title={attachment.filename}
                          style={{ border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                      </div>
                    ) : (
                      <div className="document-download">
                        <a 
                          href={getFileUrl(attachment.url)} 
                          download={attachment.filename}
                          className="btn-download-doc"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          📄 {attachment.filename} - Download ({(attachment.size / 1024).toFixed(2)} KB)
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {selectedResource.externalLink && (
              <div className="resource-external-link">
                <a
                  href={selectedResource.externalLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-external-link"
                >
                  🔗 Visit External Link
                </a>
              </div>
            )}

            {selectedResource.internalLink && selectedResource.internalLink.linkType !== 'none' && selectedResource.internalLink.linkId && (
              <div className="resource-internal-link">
                <Link
                  to={
                    selectedResource.internalLink.linkType === 'education' 
                      ? `/health-programs?category=education&program=${selectedResource.internalLink.linkId}` 
                      : selectedResource.internalLink.linkType === 'health'
                      ? `/health-programs?program=${selectedResource.internalLink.linkId}`
                      : '#'
                  }
                  className="btn-internal-link"
                >
                  {selectedResource.internalLink.linkText || 
                    (selectedResource.internalLink.linkType === 'education' ? '📚 View Related Education Program' : '🏥 View Related Health Program')
                  }
                </Link>
              </div>
            )}

            {selectedResource.tags && selectedResource.tags.length > 0 && (
              <div className="resource-tags">
                {selectedResource.tags.map((tag, idx) => (
                  <span key={idx} className="tag">#{tag}</span>
                ))}
              </div>
            )}

            {(selectedResource.effectiveDate || selectedResource.expiryDate) && (
              <div className="resource-dates">
                {selectedResource.effectiveDate && (
                  <p><strong>Effective Date:</strong> {formatDate(selectedResource.effectiveDate)}</p>
                )}
                {selectedResource.expiryDate && (
                  <p><strong>Expiry Date:</strong> {formatDate(selectedResource.expiryDate)}</p>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="resources-grid">
          {filteredResources.length === 0 ? (
            <div className="no-resources">
              <p>No resources found in this category.</p>
            </div>
          ) : (
            filteredResources.map(resource => {
              const unread = isUnreadResource(resource);

              return (
                <div 
                  key={resource._id} 
                  className={`resource-card ${resource.isFeatured ? 'featured' : ''} ${unread ? 'resource-card-unread' : ''}`}
                  onClick={() => handleSelectResource(resource)}
                >
                  {resource.isFeatured && (
                    <div className="featured-ribbon">⭐ Featured</div>
                  )}

                  {unread && (
                    <div className="new-resource-badge">● New</div>
                  )}
                  
                  {resource.thumbnailUrl && (
                    <img 
                      src={resource.thumbnailUrl} 
                      alt={resource.title}
                      className="resource-card-image"
                    />
                  )}

                  <div className="resource-card-content">
                    <div className="resource-card-header">
                      <div className="resource-title-block">
                        <h3>{resource.title}</h3>
                        {unread && <span className="new-indicator">New</span>}
                      </div>
                      <span className="category-icon">
                        {getCategoryIcon(resource.category)}
                      </span>
                    </div>

                  {resource.description && (
                    <p className="resource-card-description">{resource.description}</p>
                  )}

                    {resource.tags && resource.tags.length > 0 && (
                      <div className="resource-card-tags">
                        {resource.tags.slice(0, 3).map((tag, idx) => (
                          <span key={idx} className="tag-small">#{tag}</span>
                        ))}
                      </div>
                    )}

                    <button className="btn-view-more">View Details →</button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default CommunityResourcesPage;
